import { useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  InputAdornment,
  Select,
  Fade,
  Slide,
  Grid,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import WavingHandIcon from "@mui/icons-material/WavingHand";
import { useNavigate } from "react-router-dom";
import { countries } from "../MainScreens/CreateJweller/allCountries";
import { toast } from "react-toastify";
import Lottie from "lottie-react";
import loginAnimation from "../../assets/lottieanimations/login_screen.json";

export default function LoginPage() {
  const { t } = useTranslation();
  const [phoneOrEmail, setPhoneOrEmail] = useState("");
  const [selectedDialCode, setSelectedDialCode] = useState("+91");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleGenerateOtp = async () => {
    // if (!phoneOrEmail) {
    //   alert("Please enter your phone number.");
    //   return;
    // }

    // if (!phoneRegex.test(phoneOrEmail)) {
    //   alert("Please enter a valid phone number.");
    //   return;
    // }

    if (!phoneOrEmail) {
      toast.error(t("pages.enterPhoneOrEmail"));
      return;
    }

    if (!phoneOrEmail.includes("@")) {
      const selectedCountry = countries.find(
        (c) => c.dialCode === selectedDialCode
      );
      const maxLength = selectedCountry?.phoneLength || 10;

      if (phoneOrEmail.length !== maxLength) {
        toast.error(
          t("pages.phoneMustBeDigits", {
            maxLength,
            country: selectedCountry.countryName,
          })
        );
        return;
      }

      if (!/^[0-9]+$/.test(phoneOrEmail)) {
        toast.error(t("pages.enterValidPhone"));
        return;
      }
    }
    setLoading(true); // Start loader when button clicked

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/otp-generator/generateotp`,
        {
          otpSrc: phoneOrEmail,
          otpFor: "Admin",
          otpType: "LOGIN",
        },
        {
          headers: {
            version: "1.1.1",
          },
        }
      );

      console.log("Response:", response.data);
      setTimeout(() => {
        if (response.data?.success) {
          toast.success(response.data.data || "OTP Sent!");
          localStorage.setItem("otpPhone", phoneOrEmail);
          navigate("/GenerateOtp", { state: { phone: phoneOrEmail } });
        } else {
          toast.error(response.data?.message || "Something went wrong");
        }
        setLoading(false); // Stop loader after 3 seconds
      }, 3000);
    } catch (error) {
      console.error("Error generating OTP:", error);
      setTimeout(() => {
        toast.error("Failed to send OTP");
        setLoading(false);
      }, 3000);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Fade in={true} timeout={800}>
        <Grid container sx={{ minHeight: "100vh" }}>
          {/* Left Part - Lottie Animation */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              background: "linear-gradient(135deg, #f8f9ff 0%, #e8ebff 50%, #f0f2ff 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 3, sm: 4, md: 6 },
              position: "relative",
              order: { xs: 1, md: 1 },
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: { xs: 400, sm: 500, md: 600 },
                height: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Lottie
                animationData={loginAnimation}
                loop={true}
                style={{ width: "100%", height: "100%" }}
              />
            </Box>
          </Grid>

          {/* Right Part - Login Form */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              background: "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 3, sm: 4, md: 6 },
              order: { xs: 2, md: 2 },
            }}
          >
            <Slide direction="left" in={true} timeout={600}>
              <Box
                sx={{
                  width: "100%",
                  maxWidth: { xs: "100%", sm: 600, md: 650 },
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  justifyContent: "center",
                }}
              >
                {/* Welcome Section */}
                <Box
                  sx={{
                    textAlign: "left",
                    mb: { xs: 4, sm: 5, md: 6 },
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                        fontWeight: 700,
                        color: "#1a1a1a",
                        lineHeight: 1.2,
                      }}
                    >
                      Welcome Back
                    </Typography>
                    <WavingHandIcon
                      sx={{
                        fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                        color: "#7028DA",
                        animation: "wave 2s ease-in-out infinite",
                        "@keyframes wave": {
                          "0%, 100%": {
                            transform: "rotate(0deg)",
                          },
                          "25%": {
                            transform: "rotate(20deg)",
                          },
                          "75%": {
                            transform: "rotate(-20deg)",
                          },
                        },
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: "1rem", sm: "1.2rem" },
                      fontWeight: 400,
                      color: "#666",
                      mb: 1,
                    }}
                  >
                    We're glad to see you again!
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                      color: "#999",
                    }}
                  >
                    Please sign in to continue to your account
                  </Typography>
                </Box>

                {/* Login Form */}
                <Card
                  sx={{
                    width: "100%",
                    borderRadius: 3,
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                    background: "#ffffff",
                    border: "1px solid rgba(112, 40, 218, 0.1)",
                    transition: "all 0.3s ease",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CardContent sx={{ p: { xs: 4, sm: 5, md: 6 }, flex: 1, display: "flex", flexDirection: "column" }}>
                    {/* Header Section */}
                    <Box sx={{ mb: { xs: 4, sm: 5 }, textAlign: "left" }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: "1.5rem", sm: "1.8rem", md: "2rem" },
                          fontWeight: 600,
                          color: "#1a1a1a",
                          mb: 1,
                        }}
                      >
                        Login to Continue
                      </Typography>
                      <Box
                        component="span"
                        sx={{
                          display: "block",
                          width: "100px",
                          height: "4px",
                          background: "linear-gradient(90deg, #7028DA 0%, #9E3ED7 100%)",
                          borderRadius: 2,
                          mt: 1.5,
                        }}
                      />
                    </Box>

              {/* Text Field of Phone number */}
              <Box sx={{ mb: { xs: 4, sm: 5 } }}>
                <TextField
                  label="Email / Phone Number"
                  variant="outlined"
                  placeholder="Enter email/phone number"
                  value={phoneOrEmail}
                  onChange={(e) => setPhoneOrEmail(e.target.value)}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      backgroundColor: "#f8f9fa",
                      minHeight: "64px",
                      padding: "8px 16px",
                      fontSize: { xs: "1rem", sm: "1.1rem" },
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#ffffff",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#7028DA",
                          borderWidth: "2px",
                        },
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#ffffff",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#7028DA",
                          borderWidth: "2px",
                        },
                      },
                      "& input": {
                        padding: "12px 8px",
                        fontSize: { xs: "1rem", sm: "1.1rem" },
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: "1rem", sm: "1.1rem" },
                      "&.Mui-focused": {
                        color: "#7028DA",
                        fontSize: { xs: "1.05rem", sm: "1.15rem" },
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {phoneOrEmail.includes("@") ? (
                          <EmailIcon
                            sx={{
                              color: "#7028DA",
                              mr: 1.5,
                              fontSize: { xs: "1.75rem", sm: "2rem" },
                            }}
                          />
                        ) : (
                          <PhoneIcon
                            sx={{
                              color: "#7028DA",
                              mr: 1.5,
                              fontSize: { xs: "1.75rem", sm: "2rem" },
                            }}
                          />
                        )}
                        {!/[a-zA-Z@]/.test(phoneOrEmail) && (
                          <Select
                            value={selectedDialCode}
                            onChange={(e) => setSelectedDialCode(e.target.value)}
                            sx={{
                              "& .MuiSelect-select": {
                                py: 1,
                                pl: 0.5,
                                pr: 1,
                                border: "none",
                                fontSize: { xs: "1rem", sm: "1.1rem" },
                                "&:focus": {
                                  backgroundColor: "transparent",
                                },
                              },
                              fontSize: { xs: "1rem", sm: "1.1rem" },
                              minWidth: "90px",
                              "&:before": {
                                display: "none",
                              },
                              "&:after": {
                                display: "none",
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                              },
                            }}
                          >
                            {countries.map((c) => (
                              <MenuItem key={c.countryCode} value={c.dialCode}>
                                {`${c.dialCode} ${c.countryCode}`}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Button of Generate Otp */}
              <Button
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  background: "linear-gradient(135deg, #7028DA 0%, #9E3ED7 100%)",
                  fontWeight: 600,
                  fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.3rem" },
                  py: { xs: 2, sm: 2.25, md: 2.5 },
                  textTransform: "none",
                  borderRadius: 3,
                  boxShadow: "0 4px 15px rgba(112, 40, 218, 0.4)",
                  transition: "all 0.3s ease",
                  position: "relative",
                  overflow: "hidden",
                  minHeight: { xs: "56px", sm: "64px" },
                  "&:hover": {
                    background: "linear-gradient(135deg, #5a1fb8 0%, #8a2fc0 100%)",
                    boxShadow: "0 6px 20px rgba(112, 40, 218, 0.5)",
                    transform: "translateY(-2px)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background:
                      "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
                    transition: "left 0.5s ease",
                  },
                  "&:hover::before": {
                    left: "100%",
                  },
                }}
                onClick={handleGenerateOtp}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Generate OTP"
                )}
                </Button>
                  </CardContent>
                </Card>
              </Box>
            </Slide>
          </Grid>
        </Grid>
      </Fade>
    </Box>
  );
}
